import React, { useState, useEffect } from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import { useNavigate } from "react-router-dom";
import "../../../css/Adviser/Pages/AdviserHomePage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as RosemountInMediaIcon } from "../../../assets/icons/Rosemount in Media.svg";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";

/**
 * PersonalApproachPage component renders the "Personal Approach" page for advisers.
 *
 * This page highlights Rosemount's personal approach to working with advisers,
 * including descriptions of events, support, and values. It fetches adviser values
 * asynchronously and displays them, along with navigation, hero section, FAQ,
 * and various interactive cards and modals.
 *
 * @component
 * @returns {JSX.Element} The rendered Personal Approach page for advisers.
 *
 * @example
 * // Usage in a React Router route
 * <Route path="/Adviser/PersonalApproach" element={<PersonalApproachPage />} />
 *
 * @function
 * @name PersonalApproachPage
 */
const PersonalApproachPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  const [values, setValues] = useState([]); // start with an empty array

  useEffect(() => {
    /**
     * Asynchronously fetches adviser values and updates the state with the retrieved data.
     *
     * Calls the `getAdviserValues` function to obtain an array of adviser values,
     * then updates the local state using `setValues`.
     *
     * @async
     * @function fetchValues
     * @returns {Promise<void>} A promise that resolves when the values have been fetched and state is updated.
     */
    const fetchValues = async () => {
      const data = await getAdviserValues(); // wait for the resolved array
      setValues(data);
    };

    fetchValues();
  }, []);

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Toggles the state of the modal between open and closed.
   * Calls setIsModalOpen with the opposite of the current isModalOpen value.
   *
   * @function
   * @returns {void}
   */
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };
  return (
    <div className="Adviser-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.personalApproach}
        service={{
          name: "Personal Adviser Support",
          description:
            "Rosemount provides a personal approach to adviser support through bespoke business development meetings, adviser events, peer groups, masterclasses, compliance support and training.",
          serviceType: [
            "Personal Adviser Support",
            "Business Development Support",
            "Compliance Support",
            "Adviser Training",
            "Adviser Events",
            "Mortgage Adviser Support",
            "Financial Planner Support",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "Personal Approach",
            url: getPageUrl("/Adviser/PersonalApproach"),
          },
        ]}
        faqs={[
          {
            question: "What support does Rosemount provide to its advisers?",
            answer:
              "Rosemount offers compliance support, paraplanning, technology, business development, training programmes and marketing support for advisers.",
          },
          {
            question: "How does Rosemount offer a personal approach?",
            answer:
              "Rosemount offers a personal approach through a strong staff-to-adviser ratio, direct access to the head office team and individual support for advisers and their businesses.",
          },
          {
            question: "Does Rosemount set business targets for advisers?",
            answer:
              "Rosemount does not set fixed business targets. The network supports advisers in shaping and growing their business safely and suitably for their clients.",
          },
        ]}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["personal-approach"]}
        altText="An adviser wearing a blue suit and yellow tie sat in front of a blue background."
        isAdviser={true}
        children={
          <>
            <h1>You are your Business' biggest asset</h1>
          </>
        }
      />

      <div className="Adviser-Personal-Approach-Content">
        <h3 style={{ marginBottom: "20px" }}>
          Our focus is you. Here at Rosemount Financial Solutions (IFA) Ltd, we
          put a specific focus on the personal approach we have with our
          advisers. We provide a platform for you to reach your potential. We
          understand communication is key between advisers and their support
          network. As a family run business, we want to get to know you as a
          person. We promote a collaborative, family feel across everything we
          do as a network.
        </h3>
        <div className="Content-Large">
          As part of our network offering, we run many events throughout the
          year;
        </div>
        <div>
          <div className="Content">
            <div className="Content-Large">
              • Peer Group & Masterclass sessions
            </div>
            These are vital for continued development. These sessions involve
            presentations and interactive talks from industry experts, ensuring
            that you stay informed in the market. 
          </div>
        </div>
        <div className="Content">
          <div className="Content-Large">• Rosemount Annual Conference </div>
          We run our annual Rosemount conference every January. This is an
          opportunity to get all our advisers together at a venue in London,
          providing a chance to collaborate and learn together. 
        </div>
        <div className="Content">
          <div className="Content-Large">• Summer BBQ </div> This is a social
          event run bi-annually, welcoming our advisers and their families to a
          venue in Hampshire. Whilst we are all here to work, we are keen to
          ensure that our advisers have time for pleasure as well, and its a
          great event to network with peers, whilst enjoying a Pimm’s in the
          sunshine!
        </div>
        <div className="Content">
          <div className="Content-Large"> • Charity Ball </div> As part of
          Rosemount's commitment to supporting two local children's charities,
          we host a bi-annual Charity Ball. The Ball involves a charity auction,
          raffle, and 5 course meal, bringing together advisers, staff, families
          and providers for a night of great entertainment, raising money for
          brilliant causes.
        </div>
        <div className="Content">
          <div className="Content-Large">• Business Development Meetings </div>{" "}
          Whilst there is value in collaboration with peers, there is also value
          in individual support. Our support is bespoke to each adviser, so when
          you want to discuss future plans and growth, we will be on hand to
          have a one to one conversation about you and your business. 
        </div>
      </div>

      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
        children={
          <ButtonYellow
            buttonText="Join Us"
            onClick={() => navigate("/Adviser/JoinUs")}
          />
        }
      />
      <FAQContainer
        isAdviser={true}
        faqs={[
          {
            question: "What support does Rosemount provide to its advisers?",
            answer:
              "We offer a wide range of services, including compliance support, paraplanning, access to cutting-edge technology, business development, training programmes, and full marketing support.",
          },
          {
            question: "How do you offer a personal approach?",
            answer:
              " Our staff to adviser ratio is at 1 member of staff to 4 advisers, one of the best ratio's in the industry. You will get to know our head office team individually, and will always get through to someone you know when you call in.",
          },
          {
            question:
              "Are you going to set targets for me to do certain levels of business?",
            answer:
              "No. We are here to help you shape your business into what you want it to be. We are first and foremost here to make sure that the business you conduct is safe, and suitable for each and every client. We will work with you to help you reach the targets you set for yourself, and grow your business, your way.",
          },
        ]}
      />
      <div className="Adviser-Services-Container Adviser-Services-Holder">
        <CardProduct
          Icon={RosemountInMediaIcon}
          title="Rosemount in the Media"
          link="/RosemountInMedia"
          isAdviser={true}
        />
        <div onClick={handleToggleModal}>
          <CardProduct
            Icon={HearFromOurAdvisersIcon}
            title="Hear from our Advisers"
            isAdviser={true}
          />
        </div>
        <VideoCarouselPopup
          isOpen={isModalOpen}
          onClose={handleToggleModal}
          // videos={videos}
        />
        <CardProduct
          Icon={EventsIcon}
          title="Events"
          link="/Adviser/Events"
          isAdviser={true}
        />
      </div>

      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default PersonalApproachPage;
